import ProductCard from 'common/components/Card/ProductCard'
import Skeleton from 'common/components/Skeleton'
import Typography from 'common/components/Typography'
import { WEB_DESKTOP_RALALI_URL } from 'common/constants'
import theme from 'common/theme'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useStoreActions, useStoreState } from 'stores/hooks'
import styled from 'styled-components'

const Section = styled.section`
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  padding: 48px;
`

const TabsContainer = styled.div`
  position: relative;
  margin-top: 16px;
  margin-bottom: 32px;
  overflow: hidden;
`

const Tabs = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 12px;
  overflow-x: auto;

  &::-webkit-scrollbar {
    display: none;
  }
`

const ScrollControl = styled.button<{
  position: 'left' | 'right'
  visible: boolean
}>`
  position: absolute;
  top: 50%;
  ${(props) => (props.position === 'left' ? 'left: 0;' : 'right: 0;')}
  transform: translateY(-50%);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid ${(props) => props.theme.colors.grayLight};
  background: ${(props) => props.theme.colors.white};
  display: ${(props) => (props.visible ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;
`

const TabButton = styled.button<{ $active?: boolean }>`
  border-radius: 999px;
  border: 1px solid ${(props) => props.theme.colors.orange};
  padding: 10px 28px;
  background: ${(props) =>
    props.$active
      ? props.theme.colors.red
      : props.theme.colors.white};
  color: ${(props) =>
    props.$active
      ? props.theme.colors.white
      : props.theme.colors.black};
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: ${(props) => props.theme.colors.red};
    color: ${(props) => props.theme.colors.white};
  }
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`

const SkeletonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`

const ViewAllContainer = styled.div`
  display: flex;
  justify-content: center;
`

const ViewAll = styled.a`
  display: inline-flex;
  margin-top: 32px;
  font-weight: 700;
  color: ${(props) => props.theme.colors.orange};
  text-decoration: none;
  text-align: center;
`

function CategoriesExploration(): JSX.Element {
  const { t, i18n } = useTranslation('home')
  const { categories, isAlreadyGetCategories } = useStoreState(
    (state) => state.category,
  )
  const { itemsByCategory } = useStoreState(
    (state) => state.searchCategoryItems,
  )
  const { getCategory } = useStoreActions(
    (actions) => actions.category,
  )
  const { getItemsByCategory } = useStoreActions(
    (actions) => actions.searchCategoryItems,
  )

  const topCategories = useMemo(
    () => categories.filter((cat) => cat.cat_level_seq === 2),
    [categories],
  )
  const [activeCategory, setActiveCategory] = useState<string>()
  const [isLoadingCategory, setIsLoadingCategory] = useState<string>()
  const tabsRef = useRef<HTMLDivElement>(null)
  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(false)

  useEffect(() => {
    if (!isAlreadyGetCategories) {
      getCategory()
    }
    if (topCategories.length && !activeCategory) {
      setActiveCategory(topCategories[0].alias)
    }
  }, [
    getCategory,
    isAlreadyGetCategories,
    topCategories,
    activeCategory,
  ])

  useEffect(() => {
    if (
      activeCategory &&
      (!itemsByCategory[activeCategory] ||
        itemsByCategory[activeCategory]?.items?.length === 0)
    ) {
      setIsLoadingCategory(activeCategory)
      getItemsByCategory({ categoryAlias: activeCategory }).finally(
        () => {
          setIsLoadingCategory(undefined)
        },
      )
    }
  }, [activeCategory, getItemsByCategory, itemsByCategory])

  useEffect(() => {
    getCategory()
    if (activeCategory) {
      setIsLoadingCategory(activeCategory)
      getItemsByCategory({ categoryAlias: activeCategory }).finally(
        () => {
          setIsLoadingCategory(undefined)
        },
      )
    }
  }, [activeCategory, getCategory, getItemsByCategory, i18n.language])

  const handleTabChange = useCallback(
    (alias: string) => {
      if (alias === activeCategory) {
        return
      }
      setActiveCategory(alias)
    },
    [activeCategory],
  )

  const scrollTabs = (direction: 'left' | 'right') => {
    if (!tabsRef.current) return
    const distance = tabsRef.current.clientWidth * 0.4
    tabsRef.current.scrollBy({
      left: direction === 'left' ? -distance : distance,
      behavior: 'smooth',
    })
  }

  const updateTabIndicators = useCallback(() => {
    if (!tabsRef.current) return
    const el = tabsRef.current
    setShowLeftArrow(el.scrollLeft > 10)
    setShowRightArrow(
      el.scrollLeft + el.clientWidth < el.scrollWidth - 10,
    )
  }, [])

  useEffect(() => {
    updateTabIndicators()
    const { current } = tabsRef
    current?.addEventListener('scroll', updateTabIndicators)
    return () => {
      current?.removeEventListener('scroll', updateTabIndicators)
    }
  }, [updateTabIndicators, topCategories])

  const products = activeCategory
    ? itemsByCategory[activeCategory]?.items ?? []
    : []

  const renderSkeletons = () =>
    new Array(6).fill(0).map((_, idx) => (
      // eslint-disable-next-line react/no-array-index-key
      <Skeleton key={`cat-skel-${idx}`} width="100%" height="340px" />
    ))

  const renderProducts = (items) => {
    const limitedItems = items.slice(0, 12)

    if (!limitedItems.length) {
      return renderSkeletons()
    }

    return limitedItems.map((product) => (
      <ProductCard
        key={product.id || product.itemId}
        type="recomendation"
        image={product.imageUrl || product.image}
        name={product.name || product.itemName}
        currentPrice={String(product.priceDiscount || product.price)}
        previousPrice={String(product.price)}
        storeName={product.vendorName}
        rating={product.rating || product.ratingReview || 0}
        productURL={`${WEB_DESKTOP_RALALI_URL}${
          product.redirectUrl || `/p/${product.alias || product.id}`
        }`}
        unit={product.unitType || product.unit_type}
        isShowRating
        percentage={`${product.discountPercentage || 0}%`}
      />
    ))
  }

  return (
    <Section>
      <Typography
        type="rparagraph"
        letterSpacing="2px"
        color="orange"
        textAlign="center"
        fontSize={theme.typography.rsmall.fontSize}
      >
        {t('CATEGORY_EXPLORATION_LABEL')}
      </Typography>
      <Typography type="bh2" marginY={12} textAlign="center">
        {t('CATEGORY_EXPLORATION_TITLE')}
      </Typography>
      <Typography
        type="rparagraph"
        color="graydark"
        textAlign="center"
      >
        {t('CATEGORY_EXPLORATION_SUBTITLE')}
      </Typography>
      <TabsContainer>
        <ScrollControl
          type="button"
          position="left"
          visible={showLeftArrow}
          onClick={() => scrollTabs('left')}
        >
          ‹
        </ScrollControl>
        <Tabs ref={tabsRef}>
          {topCategories.map((category) => (
            <TabButton
              key={category.id}
              $active={category.alias === activeCategory}
              type="button"
              onClick={() => handleTabChange(category.alias)}
            >
              {category.name}
            </TabButton>
          ))}
        </Tabs>
        <ScrollControl
          type="button"
          position="right"
          visible={showRightArrow}
          onClick={() => scrollTabs('right')}
        >
          ›
        </ScrollControl>
      </TabsContainer>
      {isLoadingCategory === activeCategory ? (
        <SkeletonGrid>{renderSkeletons()}</SkeletonGrid>
      ) : (
        <Grid>
          {products.length
            ? renderProducts(products)
            : renderSkeletons()}
        </Grid>
      )}
      <ViewAllContainer>
        <ViewAll
          href={`${WEB_DESKTOP_RALALI_URL}/c/${activeCategory}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('SEE_ALL')}
        </ViewAll>
      </ViewAllContainer>
    </Section>
  )
}

export default CategoriesExploration
